import React, {
  MouseEvent,
  WheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'
import { Autoplay, EffectFade, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  CarouselControlDirection,
  CarouselControlType,
  CarouselEffectType,
  CarouselSwipeDirection,
  CarouselSwipeEffect,
  SlideInterface,
} from '@adUtilities/constants/carousel'
import { KeyPressType } from '@adUtilities/constants/common'
import { GallerySettings } from '@adUtilities/types/carousel'

import CarouselControl from './carousel-control'
import Slide from './slide'

const INITIAL_TOGGLE_CONTROLS = true

interface ComponentProps {
  gallerySettings: GallerySettings
  slides: Array<SlideInterface>
  activeSlideIndex: number
  showControl: boolean
  mousewheel?: boolean
  slideImageClass?: string
  controlType?: CarouselControlType
  controlPosition?: 'left' | 'right'
  controlDirection?: CarouselControlDirection
  showShadows?: boolean
  autoPlay?: boolean
  showBlurredBackground?: boolean
  isAppIdle?: boolean
  onContainerClick?: () => void
  getCurrentSlideIndex?: (activeSlideIndex: number) => void
  getSlideLoadingState?: (slideIndex: number) => void
  allowSlideSwiping?: boolean
  pinnedGalleryItemIndex?: number | null
  toggleControls?: boolean
  carouselControlZIndex?: string
  swipeDirection?: CarouselSwipeDirection
  swipeEffect?: CarouselSwipeEffect
  enableKeyboardControl?: boolean
  onStopSlideshow?: () => void
}

function CarouselProvider({
  gallerySettings,
  slides,
  activeSlideIndex,
  showControl,
  mousewheel = true,
  controlType,
  slideImageClass,
  controlPosition = 'left',
  controlDirection = CarouselControlDirection.Horizontal,
  showShadows = false,
  showBlurredBackground = true,
  autoPlay = false,
  isAppIdle = false,
  allowSlideSwiping = true,
  onContainerClick,
  getCurrentSlideIndex,
  getSlideLoadingState,
  toggleControls: controlledToggleControls,
  pinnedGalleryItemIndex = null,
  carouselControlZIndex,
  swipeDirection = CarouselSwipeDirection.Horizontal,
  swipeEffect = CarouselSwipeEffect.Fade,
  enableKeyboardControl = false,
  onStopSlideshow,
}: ComponentProps) {
  const swiperRef = useRef<SwiperType | null>(null)
  const pinnedGalleryItemIndexRef = useRef<number | null>(
    pinnedGalleryItemIndex
  )

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [uncontrolledToggleControls, setUncontrolledToggleControls] = useState(
    INITIAL_TOGGLE_CONTROLS
  )
  const isControlledToggleControls = controlledToggleControls !== undefined
  const toggleControls = isControlledToggleControls
    ? controlledToggleControls
    : uncontrolledToggleControls
  const [lastAutoPlayState, setLastAutoPlayState] = useState(autoPlay)

  const slideToTargetIndex = (index: number) => {
    const swiper = swiperRef.current

    if (!swiper) {
      return
    }

    swiper.allowSlideNext = true
    swiper.allowSlidePrev = true

    swiper.slideTo(index)

    swiper.allowSlideNext = false
    swiper.allowSlidePrev = false
  }

  useEffect(() => {
    const hasPinnedIndexChanged =
      pinnedGalleryItemIndexRef.current !== pinnedGalleryItemIndex

    if (hasPinnedIndexChanged && pinnedGalleryItemIndex === null) {
      swiperRef.current?.slideTo(activeSlideIndex)
    }

    if (hasPinnedIndexChanged && pinnedGalleryItemIndex) {
      slideToTargetIndex(pinnedGalleryItemIndex)
    }

    pinnedGalleryItemIndexRef.current = pinnedGalleryItemIndex
  }, [pinnedGalleryItemIndex])

  const effect = useMemo(
    () =>
      gallerySettings.effectType === 'fadeIn'
        ? CarouselEffectType.Fade
        : CarouselEffectType.Slide,
    [gallerySettings.effectType]
  )

  const goToSlide = useCallback(
    (index: number) => {
      setCurrentSlideIndex(index)
      if (pinnedGalleryItemIndexRef.current === null && swiperRef.current) {
        swiperRef.current.slideTo(index)
        return
      }
      if (getCurrentSlideIndex) {
        getCurrentSlideIndex(index)
      }
    },
    [pinnedGalleryItemIndex]
  )

  const handleMouseWheel = useCallback(
    (event: WheelEvent) => {
      if (!mousewheel) {
        return
      }

      if (event.deltaY < 0) {
        if (currentSlideIndex === 0) {
          return
        }
        goToSlide(currentSlideIndex - 1)
      } else {
        if (currentSlideIndex + 1 === slides.length) {
          return
        }
        goToSlide(currentSlideIndex + 1)
      }
    },
    [currentSlideIndex, mousewheel, slides.length]
  )

  const handleContainerClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isControlledToggleControls) {
        return
      }
      event.stopPropagation()
      if (onContainerClick) {
        onContainerClick()
      }
      setUncontrolledToggleControls((prev) => !prev)
    },
    [isControlledToggleControls]
  )

  useEffect(() => {
    if (!isControlledToggleControls) {
      setUncontrolledToggleControls(INITIAL_TOGGLE_CONTROLS)
    }
  }, [isControlledToggleControls])

  useEffect(() => {
    if (activeSlideIndex === currentSlideIndex) {
      return
    }

    const updateSlide = (index: number) => {
      setCurrentSlideIndex(index)

      const swiper = swiperRef.current
      if (!swiper) {
        return
      }

      if (pinnedGalleryItemIndex === null) {
        swiper.slideTo(index)
      } else {
        slideToTargetIndex(index)
      }
    }

    const pinnedIndex = pinnedGalleryItemIndexRef.current

    const targetIndex =
      pinnedIndex && pinnedIndex !== activeSlideIndex
        ? pinnedIndex
        : activeSlideIndex

    if (autoPlay !== lastAutoPlayState) {
      setTimeout(() => updateSlide(targetIndex), 0)
    } else {
      updateSlide(targetIndex)
    }
  }, [activeSlideIndex])

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (!enableKeyboardControl) return

      const isLeft = e.key === KeyPressType.ArrowLeft
      const isRight = e.key === KeyPressType.ArrowRight

      if (isLeft) {
        if (currentSlideIndex <= 0) {
          return
        }

        if (autoPlay && onStopSlideshow) {
          onStopSlideshow()
        }

        goToSlide(currentSlideIndex - 1)
        return
      }

      if (isRight) {
        if (currentSlideIndex + 1 >= slides.length) {
          return
        }

        if (autoPlay && onStopSlideshow) {
          onStopSlideshow()
        }

        goToSlide(currentSlideIndex + 1)
      }
    }

    document.addEventListener('keyup', keyDownHandler)
    return () => document.removeEventListener('keyup', keyDownHandler)
  }, [
    enableKeyboardControl,
    goToSlide,
    currentSlideIndex,
    slides?.length,
    autoPlay,
    onStopSlideshow,
  ])

  useEffect(() => {
    setLastAutoPlayState(autoPlay)
  }, [autoPlay])

  useEffect(() => {
    const swiper = swiperRef.current
    const pinnedIndex = pinnedGalleryItemIndexRef.current

    if (!swiper) {
      return
    }

    if (autoPlay && pinnedIndex === null) {
      swiper.autoplay.start()
    } else {
      swiper.autoplay.stop()
    }
  }, [autoPlay])

  return (
    <div
      onWheel={handleMouseWheel}
      onClick={handleContainerClick}
      className="h-full w-full"
      role="none"
    >
      <Swiper
        className="h-full w-full"
        allowSlideNext={allowSlideSwiping}
        allowSlidePrev={allowSlideSwiping}
        loop={false}
        speed={600}
        effect={effect}
        fadeEffect={{ crossFade: true }}
        autoplay={
          autoPlay
            ? {
                delay: gallerySettings.autoPlayIntervalInSeconds * 1000,
              }
            : false
        }
        slidesPerView={1}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper
        }}
        onActiveIndexChange={(swiper: SwiperType) => {
          const { realIndex } = swiper
          setCurrentSlideIndex(realIndex)
          if (getCurrentSlideIndex) {
            getCurrentSlideIndex(realIndex)
          }
        }}
        onSliderFirstMove={() => {
          if (autoPlay && onStopSlideshow) {
            onStopSlideshow()
          }
        }}
        modules={[
          Autoplay,
          swipeEffect === CarouselSwipeEffect.Glide ? Mousewheel : EffectFade,
        ]}
        direction={swipeDirection}
      >
        {slides.map((slide: SlideInterface, index: number) => (
          <SwiperSlide
            key={slide.id}
            className="relative h-full w-full overflow-hidden"
          >
            <Slide
              index={index}
              getSlideLoadingState={getSlideLoadingState}
              slide={slide}
              showBlurredBackground={showBlurredBackground}
              imageClass={slideImageClass}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {showControl && controlType && slides.length > 1 && !autoPlay && (
        <div
          className={`absolute bottom-5 ${
            controlPosition === 'left' ? 'left-6' : 'right-6'
          } transition-opacity ${
            isAppIdle || !toggleControls ? 'opacity-0' : 'opacity-100'
          } ${carouselControlZIndex ?? 'z-1'}`}
        >
          {showShadows &&
            controlDirection === CarouselControlDirection.Horizontal && (
              <div className="pointer-events-none fixed bottom-0 h-1/4 w-screen bg-shadow-horizontal" />
            )}
          <CarouselControl
            type={controlType}
            items={slides}
            currentSlideIndex={currentSlideIndex}
            toggleControls={toggleControls}
            handleItemClick={goToSlide}
            direction={controlDirection}
          />
        </div>
      )}
    </div>
  )
}

export default CarouselProvider
